import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  FC
} from 'react'
import { useSession, signOut } from 'next-auth/react'
import { useLazyQuery } from '@apollo/client'

import { GetUserDocument, Customer } from '@graphql'

interface UserContextType {
  user: Customer | null
  loadingUser: boolean
}

const UserContext = createContext<UserContextType | undefined>(undefined)

export const UserProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { data: session, status } = useSession()
  const [getUser, { data, loading }] = useLazyQuery(GetUserDocument, {
    fetchPolicy: 'cache-and-network'
  })
  const [user, setUser] = useState<Customer | null>(null)

  const viewer = data?.viewer
  const isAnonymousUser = viewer?.__typename === 'Anonymous'

  useEffect(() => {
    if (isAnonymousUser) {
      console.error(
        'Anonymous viewer detected, token may be invalid. Signing out...'
      )
      signOut({ redirect: false })
      return
    }
    if (viewer) {
      console.info('viewer', viewer)
      setUser(viewer as Customer)
    }
  }, [viewer, isAnonymousUser])

  useEffect(() => {
    if (status === 'authenticated') {
      console.info('Authenticated session detected, fetching user...')
      getUser()
    }
    if (status === 'unauthenticated') {
      setUser(null)
    }
  }, [status])

  useEffect(() => {
    if (session?.error === 'RefreshAccessTokenError') {
      console.error('Session error detected, signing out...')
      signOut({ redirect: false })
    }
  }, [session?.error])

  return (
    <UserContext.Provider value={{ user, loadingUser: loading }}>
      {children}
    </UserContext.Provider>
  )
}

export const useUserContext = () => {
  const context = useContext(UserContext)
  if (!context) {
    throw new Error('useUserContext must be used within a UserProvider')
  }
  return context
}
